import { useRouter } from "next/router";
import { type PropsWithChildren, createContext, useContext, useMemo } from "react";

import { DemoConfig } from "@/config/demo.config";
import { AuthContext } from "@/data/auth/auth.context";
import type { CeoVsEmployeeModels } from "@/data/ceoVsEmployee/ceoVsEmployee.models";
import { CommonModels } from "@/data/common/common.models";
import type { LoyaltyModels } from "@/data/loyalty/loyalty.models";
import type { ProfileModels } from "@/data/profile/profile.models";
import { ProfileDemoQueries } from "@/data/profileDemo/profileDemo.queries";
import { PublicProfileQueries } from "@/data/publicProfile/publicProfile.queries";
import type { SubscriptionsModels } from "@/data/subscriptions/subscriptions.models";
import type { WatchlistModels } from "@/data/watchlist/watchlist.models";

export namespace DemoContext {
  export interface Type {
    /**
     * Whether the app is currently in demo mode
     */
    isDemoMode: boolean;

    /**
     * The demo user ID
     */
    demoUserId: string;

    /**
     * Demo user profile data
     */
    demoProfile: ProfileModels.FullProfileResponseDto | undefined;

    /**
     * Demo user worth data (included in profile)
     */
    demoWorthData: CommonModels.WorthNumbersDto | undefined;

    /**
     * Is the demo data loading?
     */
    isLoading: boolean;

    /**
     * Mock auth context for components that need auth data
     * This simulates a logged-in, subscribed user
     */
    mockAuth: {
      user: ProfileModels.FullProfileResponseDto | undefined;
      isLoggedIn: boolean;
      subscriptionData: SubscriptionsModels.ActiveSubscriptionResponseDto | undefined;
      isInitializing: boolean;
      mockJobFitData: WatchlistModels.WatchlistJobFitDto | undefined;
      mockCeoVsEmployeeCompanies: CeoVsEmployeeModels.CeoVsEmployeeIndexDto[] | undefined;
      mockCeoVsEmployeeDetails: Map<string, CeoVsEmployeeModels.CeoVsEmployeeShowDto> | undefined;
      mockLoyaltyRewards: LoyaltyModels.RewardSummaryDto[] | undefined;
      mockLoyaltyPoints: LoyaltyModels.PointsSummary | undefined;
      mockPortfolioOverview: any;
      mockPortfolioEntries: any;
    };
  }

  const Context = createContext<Type>({} as never);

  // Mock job fit data for demo mode
  const MOCK_JOB_FIT_DATA: WatchlistModels.WatchlistJobFitDto = {
    id: "demo-job-fit-1",
    jobTitle: "Software Engineer",
    fitScore: 85,
    status: CommonModels.StatusEnum.completed,
    fitScoreBreakdown: {
      eduGrade: "A",
      expGrade: "A-",
      skillsGrade: "B+",
      certificationsGrade: "B",
      projectsGrade: "A",
    },
    actions: [
      {
        step: 1,
        title: "Enhance Your Technical Skills",
        description:
          "Consider learning advanced frameworks and tools that are commonly used in this role. Focus on modern technologies like React, TypeScript, and cloud platforms to strengthen your technical profile and increase your competitiveness.",
      },
      {
        step: 2,
        title: "Obtain Relevant Certifications",
        description:
          "Professional certifications can significantly boost your profile. Consider AWS, Google Cloud, or specialized certifications in your tech stack. These credentials demonstrate commitment to professional development and validate your expertise.",
      },
      {
        step: 3,
        title: "Build Profile Projects",
        description:
          "Showcase your abilities through real-world projects that align with this job's requirements. Create or contribute to open-source projects, build applications using relevant technologies, and document your problem-solving approach to demonstrate practical experience.",
      },
    ],
  };

  // Mock CEO vs Employee company list data for demo mode
  const MOCK_CEO_VS_EMPLOYEE_COMPANIES: CeoVsEmployeeModels.CeoVsEmployeeIndexDto[] = [
    {
      id: "demo-ceo-company-1",
      stockSymbol: "AAPL",
      company: "Apple Inc.",
      ceoSalary: 98734394,
      medianSalary: 68254,
      ceoStockIssuance: 750000000,
      medianStockIssuance: 45000,
      ceoOvercomp: 1447,
    },
    {
      id: "demo-ceo-company-2",
      stockSymbol: "MSFT",
      company: "Microsoft Corporation",
      ceoSalary: 54946310,
      medianSalary: 180000,
      ceoStockIssuance: 425000000,
      medianStockIssuance: 120000,
      ceoOvercomp: 305,
    },
    {
      id: "demo-ceo-company-3",
      stockSymbol: "GOOGL",
      company: "Alphabet Inc.",
      ceoSalary: 226006000,
      medianSalary: 295884,
      ceoStockIssuance: 1200000000,
      medianStockIssuance: 180000,
      ceoOvercomp: 764,
    },
    {
      id: "demo-ceo-company-4",
      stockSymbol: "AMZN",
      company: "Amazon.com Inc.",
      ceoSalary: 212701169,
      medianSalary: 32855,
      ceoStockIssuance: 980000000,
      medianStockIssuance: 25000,
      ceoOvercomp: 6476,
    },
    {
      id: "demo-ceo-company-5",
      stockSymbol: "TSLA",
      company: "Tesla, Inc.",
      ceoSalary: 23500000,
      medianSalary: 45811,
      ceoStockIssuance: 55800000000,
      medianStockIssuance: 32000,
      ceoOvercomp: 513,
    },
    {
      id: "demo-ceo-company-6",
      stockSymbol: "META",
      company: "Meta Platforms, Inc.",
      ceoSalary: 27110417,
      medianSalary: 296320,
      ceoStockIssuance: 890000000,
      medianStockIssuance: 175000,
      ceoOvercomp: 91,
    },
    {
      id: "demo-ceo-company-7",
      stockSymbol: "NVDA",
      company: "NVIDIA Corporation",
      ceoSalary: 32570000,
      medianSalary: 228128,
      ceoStockIssuance: 620000000,
      medianStockIssuance: 150000,
      ceoOvercomp: 143,
    },
    {
      id: "demo-ceo-company-8",
      stockSymbol: "NFLX",
      company: "Netflix, Inc.",
      ceoSalary: 38577394,
      medianSalary: 219577,
      ceoStockIssuance: 340000000,
      medianStockIssuance: 85000,
      ceoOvercomp: 176,
    },
    {
      id: "demo-ceo-company-9",
      stockSymbol: "ORCL",
      company: "Oracle Corporation",
      ceoSalary: 138246310,
      medianSalary: 156516,
      ceoStockIssuance: 890000000,
      medianStockIssuance: 95000,
      ceoOvercomp: 883,
    },
    {
      id: "demo-ceo-company-10",
      stockSymbol: "CSCO",
      company: "Cisco Systems, Inc.",
      ceoSalary: 28963420,
      medianSalary: 168900,
      ceoStockIssuance: 280000000,
      medianStockIssuance: 110000,
      ceoOvercomp: 172,
    },
    {
      id: "demo-ceo-company-11",
      stockSymbol: "INTC",
      company: "Intel Corporation",
      ceoSalary: 17886916,
      medianSalary: 124500,
      ceoStockIssuance: 185000000,
      medianStockIssuance: 68000,
      ceoOvercomp: 144,
    },
    {
      id: "demo-ceo-company-12",
      stockSymbol: "ADBE",
      company: "Adobe Inc.",
      ceoSalary: 45925000,
      medianSalary: 212380,
      ceoStockIssuance: 520000000,
      medianStockIssuance: 145000,
      ceoOvercomp: 216,
    },
    {
      id: "demo-ceo-company-13",
      stockSymbol: "CRM",
      company: "Salesforce, Inc.",
      ceoSalary: 29066182,
      medianSalary: 192851,
      ceoStockIssuance: 380000000,
      medianStockIssuance: 125000,
      ceoOvercomp: 151,
    },
    {
      id: "demo-ceo-company-14",
      stockSymbol: "JPM",
      company: "JPMorgan Chase & Co.",
      ceoSalary: 34500000,
      medianSalary: 78923,
      ceoStockIssuance: 125000000,
      medianStockIssuance: 42000,
      ceoOvercomp: 437,
    },
    {
      id: "demo-ceo-company-15",
      stockSymbol: "WMT",
      company: "Walmart Inc.",
      ceoSalary: 25678954,
      medianSalary: 27136,
      ceoStockIssuance: 95000000,
      medianStockIssuance: 15000,
      ceoOvercomp: 946,
    },
    {
      id: "demo-ceo-company-16",
      stockSymbol: "DIS",
      company: "The Walt Disney Company",
      ceoSalary: 31567084,
      medianSalary: 54256,
      ceoStockIssuance: 285000000,
      medianStockIssuance: 38000,
      ceoOvercomp: 582,
    },
    {
      id: "demo-ceo-company-17",
      stockSymbol: "BA",
      company: "The Boeing Company",
      ceoSalary: 22564125,
      medianSalary: 98746,
      ceoStockIssuance: 210000000,
      medianStockIssuance: 72000,
      ceoOvercomp: 229,
    },
    {
      id: "demo-ceo-company-18",
      stockSymbol: "V",
      company: "Visa Inc.",
      ceoSalary: 27123456,
      medianSalary: 198750,
      ceoStockIssuance: 315000000,
      medianStockIssuance: 135000,
      ceoOvercomp: 136,
    },
  ];

  // Mock CEO vs Employee detailed company data for demo mode
  const MOCK_CEO_VS_EMPLOYEE_DETAILS = new Map<string, CeoVsEmployeeModels.CeoVsEmployeeShowDto>([
    [
      "demo-ceo-company-1",
      {
        id: "demo-ceo-company-1",
        stockSymbol: "AAPL",
        company: "Apple Inc.",
        ceoName: "Tim Cook",
        ceoSalary: 98734394,
        yearsInOffice: "2011-08-24T00:00:00Z",
        founder: false,
        publicSince: "1980-12-12T00:00:00Z",
        yearFounded: "1976-04-01T00:00:00Z",
        comment: "Leading technology company known for innovative consumer electronics and software.",
        revenueBeforeHim: 65225000000,
        currentRevenue: 394328000000,
        employeeCount: 164000,
        medianSalary: 68254,
        lastYearGrowth: 2.8,
        payRatio: 1447,
        ceoOvercomp: 1447,
        medianStockIssuance: 45000,
        ceoStockIssuance: 750000000,
        gicsSector: "Information Technology",
        gicsSubIndustry: "Technology Hardware, Storage & Peripherals",
        headquarter: "Cupertino, California",
        cik: "0000320193",
        lastModified: "2024-12-01T00:00:00Z",
        latestAnnual: "2023-09-30T00:00:00Z",
        latestInterim: "2024-06-30T00:00:00Z",
        employeesAsOfDate: "2023-09-30T00:00:00Z",
        businessSummary:
          "Apple Inc. designs, manufactures, and markets smartphones, personal computers, tablets, wearables, and accessories worldwide.",
        financialSummary: "Strong financial performance with consistent revenue growth and high profit margins.",
        street: "One Apple Park Way",
        city: "Cupertino",
        state: "CA",
        postalCode: "95014",
        country: "United States",
        phone: "408-996-1010",
        website: "https://www.apple.com",
        reutersClassification: "Technology",
        businessSector: "Technology",
        industry: "Consumer Electronics",
        sector: "Technology",
        ceoAge: 63,
        ceoTitle: "Chief Executive Officer",
        ceoFirstName: "Tim",
        ceoMiddleInitial: "D",
        ceoLastName: "Cook",
        totalRsuValue: 750000000,
        enterpriseValue: 2950000000000,
        marketCap: 3010000000000,
        ttmRevenue: 394328000000,
        ttmEbitda: 129226000000,
        ttmNetIncome: 99803000000,
        ttmEps: 6.16,
        ttmRevenuePerShare: 24.41,
        bookValuePerShare: 4.382,
        ttmCashflowPerShare: 7.19,
        ttmDividendPerShare: 0.96,
        ttmGrossMargin: 45.96,
        ttmRoePercent: 160.58,
        priceToRevenue: 7.63,
        priceToEarnings: 30.12,
        priceToBook: 48.53,
        consensusRecommendation: "Buy",
        targetPrice: 220.5,
        projectedGrowthRate: 8.5,
        projectedPe: 28.5,
        projectedSales: 425000000000,
        projectedEps: 7.25,
        projectedProfit: 115000000000,
        projectedDps: 1.05,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-12-01T00:00:00Z",
      },
    ],
    [
      "demo-ceo-company-2",
      {
        id: "demo-ceo-company-2",
        stockSymbol: "MSFT",
        company: "Microsoft Corporation",
        ceoName: "Satya Nadella",
        ceoSalary: 54946310,
        yearsInOffice: "2014-02-04T00:00:00Z",
        founder: false,
        publicSince: "1986-03-13T00:00:00Z",
        yearFounded: "1975-04-04T00:00:00Z",
        comment: "Global leader in software, services, devices, and solutions.",
        revenueBeforeHim: 77849000000,
        currentRevenue: 211915000000,
        employeeCount: 221000,
        medianSalary: 180000,
        lastYearGrowth: 6.9,
        payRatio: 305,
        ceoOvercomp: 305,
        medianStockIssuance: 120000,
        ceoStockIssuance: 425000000,
        gicsSector: "Information Technology",
        gicsSubIndustry: "Systems Software",
        headquarter: "Redmond, Washington",
        cik: "0000789019",
        lastModified: "2024-12-01T00:00:00Z",
        latestAnnual: "2023-06-30T00:00:00Z",
        latestInterim: "2024-03-31T00:00:00Z",
        employeesAsOfDate: "2023-06-30T00:00:00Z",
        businessSummary:
          "Microsoft Corporation develops, licenses, and supports software, services, devices, and solutions worldwide.",
        financialSummary: "Strong cloud revenue growth with Azure leading the market in enterprise solutions.",
        street: "One Microsoft Way",
        city: "Redmond",
        state: "WA",
        postalCode: "98052",
        country: "United States",
        phone: "425-882-8080",
        website: "https://www.microsoft.com",
        reutersClassification: "Technology",
        businessSector: "Technology",
        industry: "Software",
        sector: "Technology",
        ceoAge: 56,
        ceoTitle: "Chief Executive Officer",
        ceoFirstName: "Satya",
        ceoMiddleInitial: null,
        ceoLastName: "Nadella",
        totalRsuValue: 425000000,
        enterpriseValue: 2650000000000,
        marketCap: 2890000000000,
        ttmRevenue: 211915000000,
        ttmEbitda: 96542000000,
        ttmNetIncome: 72361000000,
        ttmEps: 9.68,
        ttmRevenuePerShare: 28.42,
        bookValuePerShare: 28.64,
        ttmCashflowPerShare: 8.26,
        ttmDividendPerShare: 2.72,
        ttmGrossMargin: 68.97,
        ttmRoePercent: 36.21,
        priceToRevenue: 13.64,
        priceToEarnings: 35.87,
        priceToBook: 11.77,
        consensusRecommendation: "Buy",
        targetPrice: 425.0,
        projectedGrowthRate: 11.2,
        projectedPe: 32.5,
        projectedSales: 235000000000,
        projectedEps: 11.85,
        projectedProfit: 88000000000,
        projectedDps: 3.0,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-12-01T00:00:00Z",
      },
    ],
    [
      "demo-ceo-company-3",
      {
        id: "demo-ceo-company-3",
        stockSymbol: "GOOGL",
        company: "Alphabet Inc.",
        ceoName: "Sundar Pichai",
        ceoSalary: 226006000,
        yearsInOffice: "2015-10-02T00:00:00Z",
        founder: false,
        publicSince: "2004-08-19T00:00:00Z",
        yearFounded: "1998-09-04T00:00:00Z",
        comment: "Multinational technology conglomerate specializing in Internet-related services and products.",
        revenueBeforeHim: 74989000000,
        currentRevenue: 307394000000,
        employeeCount: 182502,
        medianSalary: 295884,
        lastYearGrowth: 8.7,
        payRatio: 764,
        ceoOvercomp: 764,
        medianStockIssuance: 180000,
        ceoStockIssuance: 1200000000,
        gicsSector: "Communication Services",
        gicsSubIndustry: "Interactive Media & Services",
        headquarter: "Mountain View, California",
        cik: "0001652044",
        lastModified: "2024-12-01T00:00:00Z",
        latestAnnual: "2023-12-31T00:00:00Z",
        latestInterim: "2024-06-30T00:00:00Z",
        employeesAsOfDate: "2023-12-31T00:00:00Z",
        businessSummary:
          "Alphabet Inc. provides various products and platforms in the United States, Europe, the Middle East, Africa, Asia-Pacific, Canada, and Latin America.",
        financialSummary: "Dominant position in digital advertising with growing cloud services and AI investments.",
        street: "1600 Amphitheatre Parkway",
        city: "Mountain View",
        state: "CA",
        postalCode: "94043",
        country: "United States",
        phone: "650-253-0000",
        website: "https://abc.xyz",
        reutersClassification: "Technology",
        businessSector: "Technology",
        industry: "Internet Services",
        sector: "Communication Services",
        ceoAge: 51,
        ceoTitle: "Chief Executive Officer",
        ceoFirstName: "Sundar",
        ceoMiddleInitial: null,
        ceoLastName: "Pichai",
        totalRsuValue: 1200000000,
        enterpriseValue: 1890000000000,
        marketCap: 2020000000000,
        ttmRevenue: 307394000000,
        ttmEbitda: 97055000000,
        ttmNetIncome: 73795000000,
        ttmEps: 5.8,
        ttmRevenuePerShare: 24.13,
        bookValuePerShare: 26.23,
        ttmCashflowPerShare: 5.92,
        ttmDividendPerShare: 0.8,
        ttmGrossMargin: 56.93,
        ttmRoePercent: 29.22,
        priceToRevenue: 6.57,
        priceToEarnings: 27.38,
        priceToBook: 6.06,
        consensusRecommendation: "Buy",
        targetPrice: 175.0,
        projectedGrowthRate: 10.5,
        projectedPe: 25.2,
        projectedSales: 340000000000,
        projectedEps: 6.95,
        projectedProfit: 88500000000,
        projectedDps: 0.88,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-12-01T00:00:00Z",
      },
    ],
  ]);

  // Mock loyalty points data for demo mode
  const MOCK_LOYALTY_POINTS: LoyaltyModels.PointsSummary = {
    currentBalance: 2450,
  };

  // Mock loyalty rewards data for demo mode
  const MOCK_LOYALTY_REWARDS: LoyaltyModels.RewardSummaryDto[] = [
    {
      id: "demo-reward-1",
      name: "Welcome Bonus",
      description: "Thank you for joining LossDog! Here's a welcome treat.",
      threshold: 0,
      imageUrl: null,
      unlockedAt: "2024-01-15T10:30:00Z",
      redeemCode: "WELCOME2024",
    },
    {
      id: "demo-reward-2",
      name: "Profile Builder",
      description: "Completed your professional profile with all details.",
      threshold: 500,
      imageUrl: null,
      unlockedAt: "2024-02-20T14:20:00Z",
      redeemCode: "PROFILE500",
    },
    {
      id: "demo-reward-3",
      name: "Portfolio Master",
      description: "Added 10+ positions to your portfolio.",
      threshold: 1000,
      imageUrl: null,
      unlockedAt: "2024-03-10T09:15:00Z",
      redeemCode: null,
    },
    {
      id: "demo-reward-4",
      name: "Watchlist Expert",
      description: "Analyzed 25+ job opportunities in your watchlist.",
      threshold: 2000,
      imageUrl: null,
      unlockedAt: "2024-04-05T16:45:00Z",
      redeemCode: null,
    },
    {
      id: "demo-reward-5",
      name: "Network Builder",
      description: "Connect with 50 professionals in your field.",
      threshold: 3000,
      imageUrl: null,
      unlockedAt: null,
      redeemCode: null,
    },
    {
      id: "demo-reward-6",
      name: "Career Strategist",
      description: "Complete all career assessment tools.",
      threshold: 5000,
      imageUrl: null,
      unlockedAt: null,
      redeemCode: null,
    },
  ];

  // Mock portfolio overview data for demo mode
  const MOCK_PORTFOLIO_OVERVIEW = {
    portfolio: {
      id: "demo-portfolio-1",
      investmentPhilosophy:
        "Tom Sosnoff's investment philosophy at Lossdog is centered on leveraging strategic investments to drive significant social and global health outcomes. He prioritizes companies with capable management and long-term growth potential. His approach is patient, often holding investments for decades, as seen with his long-term stakes in American Express and Geico.",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-12-01T00:00:00Z",
    },
    user: {
      name: "Tom Sosnoff",
      jobTitle: "CEO",
      location: "Chicago, IL, US",
      profilePictureUrl: null,
    },
    performance: {
      dayChange: null,
      totalChange: null,
      itemsCount: 19,
    },
  };

  // Mock portfolio entries data for demo mode
  const MOCK_PORTFOLIO_ENTRIES = {
    entries: [
      // Stock category (~28%)
      {
        id: "1",
        symbol: "RBLX",
        displayName: "Roblox",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Stock" as const,
        iconUrl: null,
        category: "Stock",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "2",
        symbol: "GLD",
        displayName: "Gold ETF",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Stock" as const,
        iconUrl: null,
        category: "Stock",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "3",
        symbol: "QQQ",
        displayName: "Nasdaq 100 ETF",
        positionType: "Long" as const,
        quantityPercent: 4,
        instrumentType: "Stock" as const,
        iconUrl: null,
        category: "Stock",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "4",
        symbol: "MU",
        displayName: "Micron Technology",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Stock" as const,
        iconUrl: null,
        category: "Stock",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "5",
        symbol: "CMG",
        displayName: "Chipotle",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Stock" as const,
        iconUrl: null,
        category: "Stock",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "6",
        symbol: "ZW",
        displayName: "Wheat Futures",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Futures" as const,
        iconUrl: null,
        category: "Stock",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "7",
        symbol: "ENPH",
        displayName: "Enphase Energy",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Stock" as const,
        iconUrl: null,
        category: "Stock",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "8",
        symbol: "SPY",
        displayName: "S&P 500 ETF",
        positionType: "Long" as const,
        quantityPercent: 4,
        instrumentType: "Stock" as const,
        iconUrl: null,
        category: "Stock",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      // Treasury (~23%)
      {
        id: "9",
        symbol: "ZB",
        displayName: "30-Year T-Bond Futures",
        positionType: "Long" as const,
        quantityPercent: 23,
        instrumentType: "Futures" as const,
        iconUrl: null,
        category: "Treasury",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      // Crypto (~13%)
      {
        id: "10",
        symbol: "XRP/USD",
        displayName: "Ripple",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Crypto" as const,
        iconUrl: null,
        category: "Crypto",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "11",
        symbol: "SOL/USD",
        displayName: "Solana",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Crypto" as const,
        iconUrl: null,
        category: "Crypto",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "12",
        symbol: "ETH/USD",
        displayName: "Ethereum",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Crypto" as const,
        iconUrl: null,
        category: "Crypto",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "13",
        symbol: "BTC/USD",
        displayName: "Bitcoin",
        positionType: "Long" as const,
        quantityPercent: 10,
        instrumentType: "Crypto" as const,
        iconUrl: null,
        category: "Crypto",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      // Metal (~4%)
      {
        id: "14",
        symbol: "/SI",
        displayName: "Silver Futures",
        positionType: "Long" as const,
        quantityPercent: 4,
        instrumentType: "Futures" as const,
        iconUrl: null,
        category: "Metal",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      // Currency (~4%)
      {
        id: "15",
        symbol: "/IBB",
        displayName: "British Pound Futures",
        positionType: "Long" as const,
        quantityPercent: 2,
        instrumentType: "Futures" as const,
        iconUrl: null,
        category: "Currency",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "16",
        symbol: "/AE",
        displayName: "Euro Futures",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Futures" as const,
        iconUrl: null,
        category: "Currency",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "17",
        symbol: "/6J",
        displayName: "Japanese Yen Futures",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Futures" as const,
        iconUrl: null,
        category: "Currency",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      // Energy (~2%)
      {
        id: "18",
        symbol: "/NG",
        displayName: "Natural Gas Futures",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Futures" as const,
        iconUrl: null,
        category: "Energy",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      {
        id: "19",
        symbol: "/CL",
        displayName: "Crude Oil Futures",
        positionType: "Long" as const,
        quantityPercent: 1,
        instrumentType: "Futures" as const,
        iconUrl: null,
        category: "Energy",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      // Total spent: ~67%, Cash Available: ~33%
    ],
  };

  export const Provider = ({ children }: PropsWithChildren) => {
    const router = useRouter();
    const { isInitializing, isLoggedIn } = AuthContext.useAuth();

    // Determine if we're in demo mode based on the current route
    const isDemoMode = useMemo(() => {
      const demoRoutes = Object.values(DemoConfig.routes);
      return demoRoutes.some((route) => router.pathname.startsWith(route.slice(0, -1)));
    }, [router.pathname]);

    // Fetch demo userId first when in demo mode
    const { data: demoUserData, isLoading: isLoadingUserId } = ProfileDemoQueries.useGetProfile({
      enabled: isDemoMode && !isInitializing && isLoggedIn,
    });

    // Extract userId from the response
    const demoUserId = demoUserData?.userId;

    // Fetch demo user profile data when in demo mode and userId is available
    const { data: demoProfile, isLoading: isLoadingProfile } = PublicProfileQueries.useGetProfilePublic(
      { userId: demoUserId || "" },
      { enabled: isDemoMode && !!demoUserId && !isInitializing && isLoggedIn },
    );

    // Extract worth data from profile (it's included in the public profile response)
    const demoWorthData = useMemo(() => {
      if (!demoProfile?.worthData) return undefined;

      // Transform the worth data to match the expected format
      return {
        current: demoProfile.worthData.current || 0,
        historical: demoProfile.worthData.historical || 0,
        implied: demoProfile.worthData.implied || 0,
        totalMiss: demoProfile.worthData.totalMiss || 0,
        status: CommonModels.StatusEnum.completed,
      } as CommonModels.WorthNumbersDto;
    }, [demoProfile]);

    // Create mock subscription data (always active/subscribed in demo)
    const mockSubscriptionData = useMemo((): SubscriptionsModels.ActiveSubscriptionResponseDto | undefined => {
      if (!isDemoMode || !demoProfile) return undefined;

      return {
        active: DemoConfig.features.showSubscribedFeatures,
        subscriptionId: "demo-subscription",
        productId: "demo-product",
        variantId: "demo-variant",
        status: "active",
        currentPeriodEnd: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
        currentPeriodStart: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        cancelAtPeriodEnd: false,
        trialEnd: null,
      } as SubscriptionsModels.ActiveSubscriptionResponseDto;
    }, [isDemoMode, demoProfile]);

    // Create mock auth context - cast to any to avoid strict type checking
    // The demoProfile has most of the needed fields, even if the types don't match exactly
    const mockAuth = useMemo(
      () => ({
        user: isDemoMode ? (demoProfile as any) : undefined,
        isLoggedIn: isDemoMode && !!demoProfile,
        subscriptionData: mockSubscriptionData,
        isInitializing: isDemoMode && (isLoadingUserId || isLoadingProfile),
        mockJobFitData: isDemoMode ? MOCK_JOB_FIT_DATA : undefined,
        mockCeoVsEmployeeCompanies: isDemoMode ? MOCK_CEO_VS_EMPLOYEE_COMPANIES : undefined,
        mockCeoVsEmployeeDetails: isDemoMode ? MOCK_CEO_VS_EMPLOYEE_DETAILS : undefined,
        mockLoyaltyRewards: isDemoMode ? MOCK_LOYALTY_REWARDS : undefined,
        mockLoyaltyPoints: isDemoMode ? MOCK_LOYALTY_POINTS : undefined,
        mockPortfolioOverview: isDemoMode ? MOCK_PORTFOLIO_OVERVIEW : undefined,
        mockPortfolioEntries: isDemoMode ? MOCK_PORTFOLIO_ENTRIES : undefined,
      }),
      [isDemoMode, demoProfile, mockSubscriptionData, isLoadingUserId, isLoadingProfile],
    );

    const value = useMemo(
      (): Type => ({
        isDemoMode,
        demoUserId: demoUserId || "",
        demoProfile: demoProfile as any,
        demoWorthData,
        isLoading: isLoadingUserId || isLoadingProfile,
        mockAuth,
      }),
      [isDemoMode, demoUserId, demoProfile, demoWorthData, isLoadingUserId, isLoadingProfile, mockAuth],
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
  };

  export const useDemo = () => {
    const demo = useContext(Context);
    return demo;
  };
}
